import React, { useCallback, useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faPlus, faSync, faTrash } from '@fortawesome/free-solid-svg-icons';

import { accessLocalStorage, getCarriers, deleteCarrier, formatCnpj } from '../../../services';
import { hasFeature } from '../../../services/permissions';
import ConfirmModal from '../../Common/ConfirmModal';
import Loading from '../../Loading/LoadingModule';
import {
  CARRIER_MESSAGES,
  carrierDeleteQuestion,
  carrierPermissions,
  carrierTotalLabel,
  resultErrorMessage,
} from './carrierScreen';

import '../../../libs/bulma.min.css';
import './Carriers.css';

const readSession = () => accessLocalStorage.getUserLocalStorage();

const sessionTenant = () => {
  const session = readSession();
  return session && session.user ? session.user.idtenant : null;
};

function CarriersList() {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [carriers, setCarriers] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [carrierToDelete, setCarrierToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const { canCreate, canEdit, canDelete } = carrierPermissions(hasFeature);

  const fetchCarriers = useCallback(async () => {
    const result = await getCarriers(sessionTenant());
    if (result.success) {
      setCarriers(result.data);
      setLoadError(null);
    } else {
      setCarriers([]);
      setLoadError(resultErrorMessage(result, CARRIER_MESSAGES.loadFailed));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!readSession()) {
      history.push('/');
      return;
    }
    fetchCarriers();
  }, [history, fetchCarriers]);

  const handleRefresh = () => {
    setNotice(null);
    fetchCarriers();
  };

  const openDelete = (carrier) => {
    setDeleteError(null);
    setCarrierToDelete(carrier);
  };

  const closeDelete = () => {
    if (deleting) return;
    setCarrierToDelete(null);
    setDeleteError(null);
  };

  const confirmDelete = async () => {
    if (!carrierToDelete || deleting) return;
    setDeleting(true);
    const result = await deleteCarrier(carrierToDelete.carrierId, sessionTenant());
    setDeleting(false);
    if (!result.success) {
      setDeleteError(resultErrorMessage(result, CARRIER_MESSAGES.deleteFailed));
      return;
    }
    setCarrierToDelete(null);
    setNotice(CARRIER_MESSAGES.deleted);
    await fetchCarriers();
  };

  if (loading) return <Loading />;

  return (
    <div className="carriers-container">
      <div className="carriers-header">
        <h1 className="title is-4 carriers-title">{CARRIER_MESSAGES.listTitle}</h1>
        <div className="carriers-actions">
          <button type="button" className="button is-light" onClick={handleRefresh}>
            <span className="icon"><FontAwesomeIcon icon={faSync} /></span>
            <span>{CARRIER_MESSAGES.refreshButton}</span>
          </button>
          {canCreate && (
            <Link to="/config/carriers/new" className="button is-primary">
              <span className="icon"><FontAwesomeIcon icon={faPlus} /></span>
              <span>{CARRIER_MESSAGES.newButton}</span>
            </Link>
          )}
        </div>
      </div>

      {notice && <div className="notification is-success is-light">{notice}</div>}
      {loadError && <div className="notification is-danger is-light">{loadError}</div>}

      {!loadError && carriers.length === 0 && (
        <div className="notification is-light">{CARRIER_MESSAGES.empty}</div>
      )}

      {carriers.length > 0 && (
        <>
          <div className="table-container">
            <table className="table is-fullwidth is-hoverable is-striped">
              <thead>
                <tr>
                  <th>{CARRIER_MESSAGES.nameLabel}</th>
                  <th>{CARRIER_MESSAGES.cnpjLabel}</th>
                  <th>{CARRIER_MESSAGES.actionsLabel}</th>
                </tr>
              </thead>
              <tbody>
                {carriers.map((carrier) => (
                  <tr key={carrier.carrierId}>
                    <td>{carrier.name}</td>
                    <td className="carriers-cnpj">{formatCnpj(carrier.cnpj)}</td>
                    <td>
                      <div className="carriers-row-actions">
                        {canEdit && (
                          <Link
                            to={`/config/carriers/${carrier.carrierId}/edit`}
                            className="button is-small is-info is-light"
                            title={CARRIER_MESSAGES.editTooltip}
                            aria-label={CARRIER_MESSAGES.editTooltip}
                          >
                            <FontAwesomeIcon icon={faPen} />
                          </Link>
                        )}
                        {canDelete && (
                          <button
                            type="button"
                            className="button is-small is-danger is-light"
                            title={CARRIER_MESSAGES.deleteTooltip}
                            aria-label={CARRIER_MESSAGES.deleteTooltip}
                            onClick={() => openDelete(carrier)}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="carriers-footer">{carrierTotalLabel(carriers.length)}</p>
        </>
      )}

      <ConfirmModal
        open={!!carrierToDelete}
        danger
        title={CARRIER_MESSAGES.deleteTitle}
        body={(
          <>
            <p>{carrierToDelete ? carrierDeleteQuestion(carrierToDelete.name) : ''}</p>
            {deleteError && <p className="has-text-danger mt-2"><strong>{deleteError}</strong></p>}
          </>
        )}
        confirmLabel={CARRIER_MESSAGES.deleteConfirm}
        cancelLabel={CARRIER_MESSAGES.cancelButton}
        loadingLabel={CARRIER_MESSAGES.deleteLoading}
        loading={deleting}
        onCancel={closeDelete}
        onConfirm={confirmDelete}
      />
    </div>
  );
}

export default CarriersList;
